import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlass, ChatCircleDots, Storefront, ArrowRight } from '@phosphor-icons/react';
import SearchBar from '../components/SearchBar';
import CategorySlider from '../components/CategorySlider';
import ListingCard from '../components/ListingCard';
import HeroBackground from '../components/HeroBackground';
import NewsCarousel from '../components/NewsCarousel';
import SponsorBanner from '../components/SponsorBanner';
import MarketplaceCard from '../components/MarketplaceCard';
import { fetchCategories, fetchListings } from '../lib/api';
import { fetchMarketplaceListings, type MarketplaceListing } from '../lib/marketplace-api';
import { applySeo, resetSeo } from '../lib/seo';
import { seedCategories, seedListings } from '../data/seed';
import type { Category, Listing } from '../types';

export default function Home() {
  const [categories, setCategories] = useState<Category[]>(seedCategories);
  const [listings, setListings] = useState<Listing[]>(seedListings);
  const [marketListings, setMarketListings] = useState<MarketplaceListing[]>([]);

  useEffect(() => {
    fetchCategories().then(setCategories);
    fetchListings({ sort: 'rating' }).then(setListings);
    fetchMarketplaceListings({ limit: 4 })
      .then((data) => setMarketListings(data.listings))
      .catch(() => setMarketListings([]));
  }, []);

  const featured = listings.filter(l => l.badge === 'featured' || l.badge === 'premium').slice(0, 6);

  useEffect(() => {
    applySeo({
      title: 'MarineLink - NZ Marine Directory | Charters, Marinas & Services NZ',
      description: 'MarineLink is NZ\'s marine directory: 2,200+ boat charters, marinas, chandlers, boat builders, engineers and marine services across Auckland, Northland, Waikato and every NZ region.',
      path: '/',
      keywords: 'marine directory nz, nz marine directory, marine directory new zealand, marine businesses NZ, boat charters NZ, marinas, chandlery, boat builders, marine electronics',
    });
    return () => resetSeo();
  }, []);

  return (
    <div>
      {/* Hero - left-aligned split */}
      <section className="relative min-h-[92dvh] flex items-center px-4 md:px-8 overflow-hidden w-full">
        <HeroBackground />
        <div className="relative z-10 w-full max-w-[1440px] mx-auto py-24">
          <div className="max-w-[660px]">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold leading-[1.08] tracking-tight mb-5 text-white">
              <span className="block">New Zealand's</span>
              <span className="block">marine industry,</span>
              <span className="block text-[#2ba9dc]">all in one place.</span>
            </h1>
            <p className="text-base md:text-lg text-white/75 max-w-[500px] mb-9 leading-relaxed">
              Charters, chandlers, rentals, services and marinas. Find and connect directly with NZ's best marine businesses.
            </p>
            <SearchBar variant="dark" centered={false} />
          </div>
        </div>
      </section>

      {/* Stat strip */}
      <section className="border-b border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] bg-surface-light dark:bg-surface">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 grid grid-cols-2 md:grid-cols-4">
          {[
            { value: String(listings.length), label: 'Listings' },
            { value: String(categories.length), label: 'Categories' },
            { value: '8+', label: 'NZ regions' },
            { value: '100%', label: 'Kiwi owned' },
          ].map((stat, i) => (
            <div
              key={stat.label}
              className={`py-5 md:py-6 text-center ${i > 0 ? 'border-l border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)]' : ''} ${i === 2 ? 'max-md:border-l-0 max-md:border-t max-md:border-[rgba(16,42,66,0.09)]' : ''} ${i === 3 ? 'max-md:border-t max-md:border-[rgba(16,42,66,0.09)]' : ''}`}
            >
              <div className="font-display text-xl md:text-2xl font-bold text-text-primary-light dark:text-text-primary">{stat.value}</div>
              <div className="text-[0.65rem] sm:text-xs text-text-muted uppercase tracking-wider mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Marine News Carousel */}
      <NewsCarousel />

      {/* Sponsor banner */}
      <section className="max-w-[1440px] mx-auto px-4 md:px-8 -mt-6 md:-mt-8 mb-4">
        <SponsorBanner />
      </section>

      {/* Categories */}
      <section className="max-w-[1440px] mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-7 gap-5">
          <div>
            <h2 className="font-display text-2xl md:text-[2rem] font-bold tracking-tight">Browse by category</h2>
            <p className="text-text-secondary text-sm mt-1">Find exactly what you need across New Zealand's marine industry.</p>
          </div>
          <Link to="/categories" className="text-sm font-semibold text-accent inline-flex items-center gap-1.5 hover:gap-2.5 transition-all">
            View all categories <ArrowRight size={15} weight="bold" />
          </Link>
        </div>
        <CategorySlider categories={categories} listings={listings} />
      </section>

      {/* Featured */}
      <section className="max-w-[1440px] mx-auto px-4 md:px-8 py-10 md:py-16 pt-0">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-7 gap-5">
          <div>
            <h2 className="font-display text-2xl md:text-[2rem] font-bold tracking-tight">Featured listings</h2>
            <p className="text-text-secondary text-sm mt-1">Top-rated marine businesses across Aotearoa.</p>
          </div>
          <Link to="/browse" className="text-sm font-semibold text-accent inline-flex items-center gap-1.5 hover:gap-2.5 transition-all">
            Browse all listings <ArrowRight size={15} weight="bold" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
          {featured.map((l) => <ListingCard key={l.id} listing={l} large />)}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-surface-light dark:bg-surface border-t border-b border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] py-12 md:py-16">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="font-display text-xl md:text-[2rem] font-bold tracking-tight">How MarineLink works</h2>
            <p className="text-text-secondary text-sm mt-2 max-w-[480px] mx-auto">Whether you're looking for a service or listing your business, getting started is simple.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 md:divide-x md:divide-[rgba(16,42,66,0.12)] border border-[rgba(16,42,66,0.12)] dark:border-[rgba(214,233,247,0.12)] rounded-lg bg-card-light dark:bg-card overflow-hidden">
            {[
              { icon: MagnifyingGlass, title: 'Search & discover', desc: 'Browse by category, region or keyword. Filter by rating, services and availability to find exactly what you need.' },
              { icon: ChatCircleDots, title: 'Connect directly', desc: 'Every listing has direct contact details, location and reviews. No middleman, you deal straight with the business.' },
              { icon: Storefront, title: 'List your business', desc: 'Own a marine business? Create a listing in minutes, free. Upgrade anytime for featured placement and analytics.' },
            ].map((item, i) => (
              <div key={item.title} className={`p-6 md:p-8 ${i > 0 ? 'max-md:border-t max-md:border-[rgba(16,42,66,0.12)]' : ''}`}>
                <div className="w-10 h-10 rounded-md bg-accent-glow text-accent-deep dark:text-accent-bright flex items-center justify-center mb-4">
                  <item.icon size={22} weight="regular" />
                </div>
                <h3 className="text-base font-semibold mb-2">{item.title}</h3>
                <p className="text-text-secondary text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marine Marketplace */}
      <section className="max-w-[1440px] mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-7 gap-5">
          <div>
            <h2 className="font-display text-2xl md:text-[2rem] font-bold tracking-tight">Marine Marketplace</h2>
            <p className="text-text-secondary text-sm mt-1">Boats, gear and equipment, listed directly by Kiwi boaties.</p>
          </div>
          <Link to="/marketplace" className="text-sm font-semibold text-accent inline-flex items-center gap-1.5 hover:gap-2.5 transition-all">
            Browse marketplace <ArrowRight size={15} weight="bold" />
          </Link>
        </div>
        {marketListings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {marketListings.map((l) => <MarketplaceCard key={l.id} listing={l} />)}
          </div>
        ) : (
          <div className="bg-card-light dark:bg-card border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] rounded-lg p-8 text-center">
            <p className="text-text-secondary text-sm mb-4">
              The marketplace is just getting started. Got a boat, motor or gear to sell?
            </p>
            <Link to="/marketplace" className="btn btn-primary btn-sm">
              Sell your gear
            </Link>
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="max-w-[1440px] mx-auto px-4 md:px-8 pb-12 md:pb-20">
        <div className="bg-surface-light dark:bg-surface border border-[rgba(79,179,217,0.2)] dark:border-[rgba(79,179,217,0.2)] rounded-xl p-8 md:p-12 text-center">
          <h2 className="font-display text-xl md:text-[2rem] font-bold mb-3 tracking-tight">Ready to reach more customers?</h2>
          <p className="text-text-secondary max-w-[480px] mx-auto mb-7 text-sm md:text-base px-2">
            List your marine business free and get found by thousands of boaties, sailors and maritime professionals across New Zealand.
          </p>
          <Link to="/list" className="btn btn-primary text-sm md:text-base">
            List your business
          </Link>
        </div>
      </section>
    </div>
  );
}
